import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { ILogger, LoggerSymbol, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { ExternalComponentLoaderSymbol, name } from '../symbols'
import {
	ExternalComponentSiteConfig,
	IExternalComponentDsApi,
	IExternalComponentLoader,
	IExternalComponentProps,
} from '../types'
import { ComponentsStore, ComponentsStoreSymbol, ViewerComponent } from 'feature-components'
import { loadComponentAndUpdateProps } from '../utils/loadComponentAndUpdateProps'

export const ExternalComponentDsApiFactory = withDependencies(
	[named(SiteFeatureConfigSymbol, name), ExternalComponentLoaderSymbol, ComponentsStoreSymbol, LoggerSymbol],
	(
		{ disableBYOC }: ExternalComponentSiteConfig,
		externalComponentLoader: IExternalComponentLoader,
		componentsStore: ComponentsStore,
		logger: ILogger
	): IExternalComponentDsApi => {
		const lastCacheKillerForUrl: Record<string, number> = {}

		// reloadExternalComponent can be called from two flows:
		// 1. component url changes
		// 2. cacheKiller updates (when files content changed)
		// getNextCacheKiller will prevent redundant fetches in case of component url changed for files
		// updated in the current editor session (will reuse the previous cacheKillerCounter)
		const getNextCacheKiller = (url: string, cacheKillerCounter?: number) => {
			if (typeof cacheKillerCounter === 'undefined') {
				return lastCacheKillerForUrl[url]
			}

			lastCacheKillerForUrl[url] = cacheKillerCounter
			return cacheKillerCounter
		}
		const onError = (e: Error) => {
			logger.captureError(e, {
				tags: {
					feature: 'viewer-external-component',
					error: e.message,
					type: e.name,
				},
			})
		}

		return {
			reloadExternalComponent: async ({ compId, data, cacheKillerCounter }) => {
				if (disableBYOC) {
					return
				}
				const comp = componentsStore.get<ViewerComponent<IExternalComponentProps>>(compId)

				const nextCacheKillerCounter = getNextCacheKiller(data.url, cacheKillerCounter)
				await loadComponentAndUpdateProps(
					externalComponentLoader,
					comp,
					data,
					disableBYOC,
					onError,
					nextCacheKillerCounter
				)
			},
		}
	}
)
